import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Save, FileJson, Layers, Upload, MousePointer2, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react';
import {
  normalizeTilesetConfig,
  resolveTilesetImageSrc,
  resolveTerrainSourceRect,
  recommendTerrainRenderMode,
  normalizeTerrainRenderMode
} from '../../data/tilesets';
import adminService from '../../services/adminService';

const TilesetConfigurator = ({
  value,
  onSave,
  isSaving = false,
}) => {
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch (_) {
        return null;
      }
    }
    return value;
  }, [value]);

  const initialConfig = useMemo(() => normalizeTilesetConfig(parsedValue), [parsedValue]);
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  const [config, setConfig] = useState(initialConfig);
  const [jsonPreview, setJsonPreview] = useState(JSON.stringify(initialConfig, null, 2));
  const [error, setError] = useState('');
  const [previewSrc, setPreviewSrc] = useState(resolveTilesetImageSrc(initialConfig.image, apiBaseUrl));
  const [previewImageSize, setPreviewImageSize] = useState({ width: 0, height: 0 });
  const [selectedCell, setSelectedCell] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [activeTerrainIndex, setActiveTerrainIndex] = useState(0);
  const [selectedTerrainVariantMode, setSelectedTerrainVariantMode] = useState('auto');
  const [selectedTerrainVariantIndex, setSelectedTerrainVariantIndex] = useState(0);
  const [expandedTerrainIndex, setExpandedTerrainIndex] = useState(0);
  const [showJsonEditor, setShowJsonEditor] = useState(false);
  const [testMapMode, setTestMapMode] = useState('paint');
  const [testMapSize] = useState({ width: 10, height: 8 });
  const [testMap, setTestMap] = useState(() => Array.from({ length: 80 }, () => null));
  const [uploading, setUploading] = useState(false);
  const canvasRef = useRef(null);
  const testMapRef = useRef(null);
  const terrainById = useMemo(() => {
    const map = new Map();
    (config.terrains || []).forEach((terrain) => {
      map.set(Number(terrain.terrainId), terrain);
    });
    return map;
  }, [config.terrains]);

  useEffect(() => {
    const next = normalizeTilesetConfig(parsedValue);
    setConfig(next);
    setJsonPreview(JSON.stringify(next, null, 2));
    setPreviewSrc(resolveTilesetImageSrc(next.image, apiBaseUrl));
    setExpandedTerrainIndex((current) => {
      if (!next.terrains.length) return -1;
      return Math.min(current, next.terrains.length - 1);
    });
    setSelectedTerrainVariantIndex((current) => {
      const activeTerrain = next.terrains[0];
      const cells = Array.isArray(activeTerrain?.cells) ? activeTerrain.cells : [];
      if (!cells.length) return 0;
      return Math.min(current, cells.length - 1);
    });
    setSelectedTerrainVariantMode('auto');
  }, [parsedValue, apiBaseUrl]);

  useEffect(() => {
    const cells = getActiveTerrainVariantCells();
    setSelectedTerrainVariantIndex((current) => {
      if (!cells.length) return 0;
      return Math.min(current, cells.length - 1);
    });
    setSelectedTerrainVariantMode('auto');
  }, [activeTerrainIndex, config.terrains]);

  useEffect(() => {
    setJsonPreview(JSON.stringify(config, null, 2));
    setPreviewSrc(resolveTilesetImageSrc(config.image, apiBaseUrl));
  }, [config, apiBaseUrl]);

  useEffect(() => {
    const img = new window.Image();
    img.onload = () => setPreviewImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.src = previewSrc;
  }, [previewSrc]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !previewImageSize.width || !previewImageSize.height) return;
    const ctx = canvas.getContext('2d');
    const tileSize = Number(config.tileSize || 48);
    const cols = Math.max(1, Math.floor(previewImageSize.width / tileSize));
    const rows = Math.max(1, Math.floor(previewImageSize.height / tileSize));
    canvas.width = previewImageSize.width;
    canvas.height = previewImageSize.height;
      const img = new window.Image();
      img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(15, 23, 42, 0.12)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = 'rgba(255,255,255,0.18)';
      for (let x = 0; x <= cols; x++) {
        ctx.beginPath();
        ctx.moveTo(x * tileSize, 0);
        ctx.lineTo(x * tileSize, rows * tileSize);
        ctx.stroke();
      }
      for (let y = 0; y <= rows; y++) {
        ctx.beginPath();
        ctx.moveTo(0, y * tileSize);
        ctx.lineTo(cols * tileSize, y * tileSize);
        ctx.stroke();
      }
      const activeTerrain = config.terrains[activeTerrainIndex];
      const cells = activeTerrain?.cells || [];
      cells.forEach((cell) => {
        ctx.fillStyle = 'rgba(34, 211, 238, 0.20)';
        ctx.fillRect(cell.x * tileSize, cell.y * tileSize, tileSize, tileSize);
        ctx.strokeStyle = '#22D3EE';
        ctx.lineWidth = 2;
        ctx.strokeRect(cell.x * tileSize + 1, cell.y * tileSize + 1, tileSize - 2, tileSize - 2);
      });

      const sx = selectedCell.x * tileSize;
      const sy = selectedCell.y * tileSize;
      ctx.fillStyle = 'rgba(251, 191, 36, 0.25)';
      ctx.fillRect(sx, sy, tileSize, tileSize);
      ctx.strokeStyle = '#FBBF24';
      ctx.lineWidth = 3;
      ctx.strokeRect(sx + 1, sy + 1, tileSize - 2, tileSize - 2);

      if (dragStart && dragEnd) {
        const minX = Math.min(dragStart.x, dragEnd.x);
        const minY = Math.min(dragStart.y, dragEnd.y);
        const maxX = Math.max(dragStart.x, dragEnd.x);
        const maxY = Math.max(dragStart.y, dragEnd.y);
        ctx.fillStyle = 'rgba(236, 72, 153, 0.18)';
        ctx.fillRect(minX * tileSize, minY * tileSize, (maxX - minX + 1) * tileSize, (maxY - minY + 1) * tileSize);
        ctx.strokeStyle = '#EC4899';
        ctx.lineWidth = 3;
        ctx.strokeRect(minX * tileSize + 1, minY * tileSize + 1, (maxX - minX + 1) * tileSize - 2, (maxY - minY + 1) * tileSize - 2);
      }
    };
    img.src = previewSrc;
  }, [previewSrc, previewImageSize, config.tileSize, selectedCell, dragStart, dragEnd, config.terrains, activeTerrainIndex]);

  useEffect(() => {
    const canvas = testMapRef.current;
    if (!canvas || !previewSrc) return;
    const ctx = canvas.getContext('2d');
    const tileSize = Number(config.tileSize || 48);
    const cols = testMapSize.width;
    const rows = testMapSize.height;
    canvas.width = cols * tileSize;
    canvas.height = rows * tileSize;
    const img = new window.Image();
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const cellValue = getSelectedTestMapCell(testMap[y * cols + x]);
          if (cellValue === null) {
            ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
            ctx.fillRect(x * tileSize, y * tileSize, tileSize, tileSize);
          } else {
            const terrain = config.terrains[cellValue.terrainIndex];
            const source = resolveTerrainSourceRect({
              terrain,
              terrainType: cellValue.terrainIndex,
              x,
              y,
              tileSize,
              renderMode: normalizeTerrainRenderMode(terrain),
              variantMode: cellValue.variantMode || 'auto',
              variantIndex: cellValue.variantIndex,
              variantCells: terrain?.cells || [],
              terrainById,
              getNeighborTerrainType: (nx, ny) => {
                if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) return null;
                const neighbor = getSelectedTestMapCell(testMap[ny * cols + nx]);
                return neighbor?.terrainIndex ?? null;
              }
            });
            ctx.drawImage(
              img,
              source.x,
              source.y,
              tileSize,
              tileSize,
              x * tileSize,
              y * tileSize,
              tileSize,
              tileSize
            );
          }
          ctx.strokeStyle = 'rgba(148, 163, 184, 0.22)';
          ctx.strokeRect(x * tileSize + 0.5, y * tileSize + 0.5, tileSize - 1, tileSize - 1);
        }
      }
    };
    img.onerror = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const cell = testMap[y * cols + x];
          ctx.fillStyle = cell === null || cell === undefined ? 'rgba(15, 23, 42, 0.9)' : 'rgba(34, 211, 238, 0.22)';
          ctx.fillRect(x * tileSize, y * tileSize, tileSize, tileSize);
          ctx.strokeStyle = 'rgba(148, 163, 184, 0.22)';
          ctx.strokeRect(x * tileSize + 0.5, y * tileSize + 0.5, tileSize - 1, tileSize - 1);
        }
      }
    };
    img.src = previewSrc;
  }, [previewSrc, config.tileSize, config.terrains, testMap, testMapSize.width, testMapSize.height]);

  const updateTerrain = (index, patch) => {
    setConfig(prev => {
      const terrains = prev.terrains.slice();
      terrains[index] = { ...terrains[index], ...patch };
      return { ...prev, terrains };
    });
  };

  const addTerrain = () => {
    setConfig(prev => {
      const nextIndex = prev.terrains.length;
      setExpandedTerrainIndex(nextIndex);
      setActiveTerrainIndex(nextIndex);
      const newTerrain = {
        ...DEFAULT_TERRAIN,
        terrainId: Date.now() % 100000,
        name: 'Nuevo terreno'
      };
      return {
        ...prev,
        terrains: [
          ...prev.terrains,
          {
            ...newTerrain,
            renderMode: recommendTerrainRenderMode(newTerrain)
          }
        ]
      };
    });
  };

  const assignSelectedCellToTerrain = () => {
    const tileSize = Number(config.tileSize || 48);
    const frame = {
      x: Number(selectedCell.x),
      y: Number(selectedCell.y)
    };
    updateTerrain(activeTerrainIndex, {
      frame,
      cells: [{ x: selectedCell.x, y: selectedCell.y }],
      autotile: false,
      renderMode: 'fixed'
    });
  };

  const getActiveTerrainVariantCells = () => {
    const terrain = config.terrains[activeTerrainIndex];
    return Array.isArray(terrain?.cells) ? terrain.cells : [];
  };

  const getSelectedTestMapCell = (cellValue) => {
    if (cellValue === null || cellValue === undefined) return null;
    if (typeof cellValue === 'object') {
      return {
        terrainIndex: Number(cellValue.terrainIndex ?? cellValue.terrain ?? 0),
        variantMode: cellValue.variantMode || 'auto',
        variantIndex: cellValue.variantIndex === null || cellValue.variantIndex === undefined
          ? null
          : Number(cellValue.variantIndex)
      };
    }
    return { terrainIndex: Number(cellValue), variantMode: 'auto', variantIndex: null };
  };

  const assignSelectionRectToTerrain = () => {
    if (!dragStart || !dragEnd) return;
    const minX = Math.min(dragStart.x, dragEnd.x);
    const minY = Math.min(dragStart.y, dragEnd.y);
    const maxX = Math.max(dragStart.x, dragEnd.x);
    const maxY = Math.max(dragStart.y, dragEnd.y);
    const cells = [];
    for (let y = minY; y <= maxY; y++) {
      for (let x = minX; x <= maxX; x++) {
        cells.push({ x, y });
      }
    }
    updateTerrain(activeTerrainIndex, {
      cells,
      frame: { x: minX, y: minY },
      autotile: false,
      renderMode: 'hybrid'
    });
    setDragStart(null);
    setDragEnd(null);
  };

  const paintTestMapCell = (x, y) => {
    const cols = testMapSize.width;
    if (x < 0 || y < 0 || x >= cols || y >= testMapSize.height) return;
    setTestMap((prev) => {
      const next = prev.slice();
      next[y * cols + x] = testMapMode === 'erase'
        ? null
        : {
            terrainIndex: activeTerrainIndex,
            variantMode: selectedTerrainVariantMode,
            variantIndex: selectedTerrainVariantMode === 'manual' && getActiveTerrainVariantCells().length > 0
              ? selectedTerrainVariantIndex
              : null
          };
      return next;
    });
  };


  const fillTestMap = () => {
    setTestMap(Array.from({ length: testMapSize.width * testMapSize.height }, () => (
      testMapMode === 'erase'
        ? null
        : {
            terrainIndex: activeTerrainIndex,
            variantMode: selectedTerrainVariantMode,
            variantIndex: selectedTerrainVariantMode === 'manual' && getActiveTerrainVariantCells().length > 0
              ? selectedTerrainVariantIndex
              : null
          }
    )));
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const result = await adminService.uploadImage(file);
      const filePath = result.url || `/uploads/gallery/${result.name}`;
      setConfig(prev => ({ ...prev, image: filePath }));
      setPreviewSrc(resolveTilesetImageSrc(filePath, apiBaseUrl));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'No se pudo subir la imagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeTerrain = (index) => {
    setConfig(prev => ({
      ...prev,
      terrains: prev.terrains.filter((_, i) => i !== index)
    }));
    setExpandedTerrainIndex((current) => {
      if (current === index) return -1;
      if (current > index) return current - 1;
      return current;
    });
    setActiveTerrainIndex((current) => {
      if (current === index) return 0;
      if (current > index) return current - 1;
      return current;
    });
  };

  const clearTestMap = () => {
    setTestMap(Array.from({ length: testMapSize.width * testMapSize.height }, () => null));
  };

  const handleJsonChange = (text) => {
    setJsonPreview(text);
    try {
      const parsed = normalizeTilesetConfig(JSON.parse(text));
      setConfig(parsed);
      setError('');
    } catch (err) {
      setError('JSON inválido: revisa la sintaxis antes de guardar.');
    }
  };

  const handleSave = () => {
    try {
      const parsed = normalizeTilesetConfig(JSON.parse(jsonPreview));
      setConfig(parsed);
      setError('');
      onSave(parsed);
    } catch (err) {
      setError('No se puede guardar: el JSON contiene errores.');
    }
  };

  return (
    <div className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-2xl overflow-hidden">
      <div className="p-6 border-b border-slate-800 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <Layers size={20} /> Configuración manual de terrenos
          </h3>
          <p className="text-sm text-slate-400">
            Edita el JSON del tileset y define cada terreno manualmente.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="px-4 py-3 rounded-xl bg-game-primary hover:bg-game-primary/80 disabled:opacity-50 text-white font-bold flex items-center gap-2 transition-colors"
        >
          <Save size={16} /> {isSaving ? 'Guardando...' : 'Guardar tileset'}
        </button>
      </div>

      <div className="p-6 grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-6">
        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 space-y-3">
            <div className="flex items-center gap-2 text-slate-300 font-bold text-sm">
              <FileJson size={16} /> Metadatos
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-slate-400 space-y-1">
                <span className="block font-bold uppercase">ID</span>
                <input value={config.id || ''} onChange={(e) => setConfig(prev => ({ ...prev, id: e.target.value }))} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
              </label>
              <label className="text-xs text-slate-400 space-y-1">
                <span className="block font-bold uppercase">Nombre</span>
                <input value={config.name || ''} onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
              </label>
              <label className="text-xs text-slate-400 space-y-1">
                <span className="block font-bold uppercase">Imagen</span>
                <input value={config.image || ''} onChange={(e) => setConfig(prev => ({ ...prev, image: e.target.value }))} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer">
                  <Upload size={14} />
                  {uploading ? 'Subiendo...' : 'Subir desde PC'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleUploadImage} />
                </label>
              </label>
              <label className="text-xs text-slate-400 space-y-1">
                <span className="block font-bold uppercase">Tile size</span>
                <input type="number" value={config.tileSize || 48} onChange={(e) => setConfig(prev => ({ ...prev, tileSize: Number(e.target.value) }))} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
              </label>
              <label className="text-xs text-slate-400 space-y-1">
                <span className="block font-bold uppercase">Render size</span>
                <input type="number" value={config.renderSize || 32} onChange={(e) => setConfig(prev => ({ ...prev, renderSize: Number(e.target.value) }))} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
              </label>
              <label className="text-xs text-slate-400 space-y-1">
                <span className="block font-bold uppercase">Columnas</span>
                <input type="number" value={config.columns || 16} onChange={(e) => setConfig(prev => ({ ...prev, columns: Number(e.target.value) }))} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
              </label>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="text-slate-300 font-bold text-sm">Terrenos</div>
                <span className="px-2 py-1 rounded-full bg-slate-900/70 border border-slate-700 text-[10px] font-bold text-slate-300">
                  {config.terrains.length} total
                </span>
                <span className="px-2 py-1 rounded-full bg-game-primary/15 border border-game-primary/30 text-[10px] font-bold text-game-primary">
                  {config.terrains.filter((terrain) => terrain.autotile).length} autotile
                </span>
              </div>
              <button
                type="button"
                onClick={addTerrain}
                className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-2"
              >
                <Plus size={14} /> Añadir
              </button>
            </div>
            <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1 custom-scrollbar">
              {config.terrains.map((terrain, index) => {
                const isExpanded = expandedTerrainIndex === index;
                return (
                  <div
                    key={`${terrain.terrainId}-${index}`}
                    className={`rounded-xl border p-3 space-y-3 transition-colors ${
                      isExpanded ? 'border-game-primary/50 bg-slate-900/60' : 'border-slate-800 bg-slate-900/35'
                    }`}
                  >
                    <div className="w-full flex items-center justify-between gap-3 text-left">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl border border-slate-700 bg-slate-950/70 overflow-hidden flex items-center justify-center">
                            <div className="grid grid-cols-2 gap-[2px] p-1 w-full h-full">
                              <div className="rounded bg-cyan-500/70" />
                              <div className="rounded bg-slate-700/70" />
                              <div className="rounded bg-slate-700/70" />
                              <div className="rounded bg-fuchsia-500/70" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                          <div className="text-xs font-bold text-slate-100 truncate">
                            {terrain.name?.trim() || `Terreno #${index + 1}`}
                          </div>
                          {activeTerrainIndex === index && (
                            <span className="px-2 py-0.5 rounded-full bg-game-primary/15 text-[10px] font-bold text-game-primary border border-game-primary/30">
                              Activo
                            </span>
                          )}
                            </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-slate-400">
                          <span>ID {terrain.terrainId}</span>
                          <span>•</span>
                          <span>{(terrain.cells || []).length} celdas</span>
                          <span>•</span>
                          <span>{normalizeTerrainRenderMode(terrain) === 'autotile' ? 'Autotile' : normalizeTerrainRenderMode(terrain) === 'hybrid' ? 'Hibrido' : 'Fijo'}</span>
                          <span>•</span>
                          <span>{terrain.walkable ? 'Pasable' : 'Obstáculo'}</span>
                        </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeTerrain(index);
                          }}
                          className="text-rose-400 hover:text-rose-300"
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setExpandedTerrainIndex((current) => (current === index ? -1 : index))}
                          className="text-slate-400 hover:text-slate-200"
                          aria-label={isExpanded ? 'Contraer terreno' : 'Expandir terreno'}
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="space-y-3 pt-1">
                        <button
                          type="button"
                          onClick={() => setActiveTerrainIndex(index)}
                          className={`w-full px-3 py-2 rounded-lg border text-xs font-bold flex items-center justify-center gap-2 transition-colors ${
                            activeTerrainIndex === index ? 'bg-game-primary/15 border-game-primary/50 text-game-primary' : 'bg-slate-900/40 border-slate-700 text-slate-300'
                          }`}
                        >
                          <MousePointer2 size={14} />
                          Editando con clic
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setDragStart(null);
                            setDragEnd(null);
                            updateTerrain(index, { cells: terrain.cells || [] });
                            setActiveTerrainIndex(index);
                          }}
                          className="w-full px-3 py-2 rounded-lg border text-[11px] font-bold bg-slate-900/30 border-slate-700 text-slate-300"
                        >
                          Celdas asociadas: {(terrain.cells || []).length}
                        </button>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">terrainId</span>
                            <input type="number" value={terrain.terrainId} onChange={(e) => updateTerrain(index, { terrainId: Number(e.target.value) })} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
                          </label>
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Nombre</span>
                            <input value={terrain.name} onChange={(e) => updateTerrain(index, { name: e.target.value })} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
                          </label>
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Mapa de máscara</span>
                            <select value={terrain.maskMap || 'default'} onChange={(e) => updateTerrain(index, { maskMap: e.target.value })} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white">
                              <option value="default">Default</option>
                              <option value="road">Road</option>
                              <option value="water">Water</option>
                              <option value="beach">Beach</option>
                              <option value="lake">Lake</option>
                              <option value="wall">Wall</option>
                              <option value="forest">Forest</option>
                              <option value="swamp">Swamp</option>
                            </select>
                          </label>
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Modo visual</span>
                            <select
                              value={normalizeTerrainRenderMode(terrain)}
                              onChange={(e) => updateTerrain(index, {
                                renderMode: e.target.value,
                                autotile: e.target.value !== 'fixed'
                              })}
                              className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white"
                            >
                              <option value="fixed">Fijo</option>
                              <option value="autotile">Autotile</option>
                              <option value="hybrid">Hibrido</option>
                            </select>
                          </label>
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Autotile row</span>
                            <input type="number" value={terrain.autotileRow || 0} onChange={(e) => updateTerrain(index, { autotileRow: Number(e.target.value) })} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
                          </label>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Frame X</span>
                            <input type="number" value={terrain.frame?.x || 0} onChange={(e) => updateTerrain(index, { frame: { ...terrain.frame, x: Number(e.target.value) } })} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
                          </label>
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Frame Y</span>
                            <input type="number" value={terrain.frame?.y || 0} onChange={(e) => updateTerrain(index, { frame: { ...terrain.frame, y: Number(e.target.value) } })} className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white" />
                          </label>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
                          <label className="text-[10px] text-slate-400 space-y-1">
                            <span className="block uppercase font-bold">Paso</span>
                            <select
                              value={terrain.walkable ? 'walkable' : 'blocked'}
                              onChange={(e) => updateTerrain(index, { walkable: e.target.value === 'walkable' })}
                              className="w-full bg-slate-900/60 border border-slate-700 rounded-lg p-2 text-white"
                            >
                              <option value="walkable">Pasable</option>
                              <option value="blocked">Obstáculo</option>
                            </select>
                          </label>
                          <label className="flex items-center gap-2 pt-5">
                            <input type="checkbox" checked={normalizeTerrainRenderMode(terrain) !== 'fixed'} onChange={(e) => updateTerrain(index, { renderMode: e.target.checked ? (terrain.cells?.length > 0 ? 'hybrid' : 'autotile') : 'fixed', autotile: e.target.checked })} />
                            Autotile
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </aside>

        <section className="space-y-4">
          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-300 font-bold text-sm">Preview visual del tileset</div>
              <span className="text-[10px] uppercase tracking-widest text-slate-500">Haz clic para marcar celdas</span>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_260px] gap-4">
              <div className="space-y-3">
                <div className="overflow-auto max-h-[720px] rounded-2xl border border-slate-800 bg-slate-950/60">
                  <canvas
                    ref={canvasRef}
                    onMouseDown={(e) => {
                      const canvas = canvasRef.current;
                      const rect = canvas.getBoundingClientRect();
                      const scaleX = canvas.width / rect.width;
                      const scaleY = canvas.height / rect.height;
                      const x = Math.floor(((e.clientX - rect.left) * scaleX) / Number(config.tileSize || 48));
                      const y = Math.floor(((e.clientY - rect.top) * scaleY) / Number(config.tileSize || 48));
                      setSelectedCell({ x, y });
                      setDragStart({ x, y });
                      setDragEnd({ x, y });
                    }}
                    onMouseMove={(e) => {
                      if (e.buttons !== 1 || !dragStart) return;
                      const canvas = canvasRef.current;
                      const rect = canvas.getBoundingClientRect();
                      const scaleX = canvas.width / rect.width;
                      const scaleY = canvas.height / rect.height;
                      const x = Math.floor(((e.clientX - rect.left) * scaleX) / Number(config.tileSize || 48));
                      const y = Math.floor(((e.clientY - rect.top) * scaleY) / Number(config.tileSize || 48));
                      setDragEnd({ x, y });
                      setSelectedCell({ x, y });
                    }}
                    onMouseUp={() => {
                      if (dragStart && dragEnd) assignSelectionRectToTerrain();
                      else setDragStart(null);
                    }}
                    className="w-full h-auto cursor-crosshair"
                  />
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                  <span className="px-2 py-1 rounded-lg bg-slate-900/60 border border-slate-700">Celda: {selectedCell.x}, {selectedCell.y}</span>
                  <span className="px-2 py-1 rounded-lg bg-slate-900/60 border border-slate-700">Terreno activo: {config.terrains[activeTerrainIndex]?.name || '-'}</span>
                  <button
                    type="button"
                    onClick={assignSelectedCellToTerrain}
                    className="px-3 py-1 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-bold inline-flex items-center gap-2"
                  >
                    <ImageIcon size={14} /> Asignar celda a terreno
                  </button>
                  <button
                    type="button"
                    onClick={assignSelectionRectToTerrain}
                    className="px-3 py-1 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-bold inline-flex items-center gap-2"
                  >
                    <Layers size={14} /> Asignar bloque
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3 text-xs text-slate-300">
                  <div className="font-bold text-slate-200 mb-2">Uso rápido</div>
                  <ul className="space-y-2 leading-relaxed">
                    <li>1. Sube una imagen de tileset desde tu PC.</li>
                    <li>2. Ajusta el tamaño del tile.</li>
                    <li>3. Haz clic en una celda del preview.</li>
                    <li>4. Pulsa <b>Asignar celda a terreno</b>.</li>
                  </ul>
                </div>
                <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-3 text-xs text-slate-300">
                  <div className="font-bold text-slate-200 mb-2">Terreno seleccionado</div>
                  <div className="mb-3">{config.terrains[activeTerrainIndex]?.name || 'Ninguno'}</div>
                  {getActiveTerrainVariantCells().length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedTerrainVariantMode('auto')}
                          className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${selectedTerrainVariantMode === 'auto'
                              ? 'bg-game-primary text-white'
                              : 'bg-slate-800 text-slate-300 hover:text-white'
                            }`}
                        >
                          Auto
                        </button>
                        <button
                          type="button"
                          onClick={() => setSelectedTerrainVariantMode('manual')}
                          className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest transition-all ${selectedTerrainVariantMode === 'manual'
                              ? 'bg-fuchsia-500 text-white'
                              : 'bg-slate-800 text-slate-300 hover:text-white'
                            }`}
                        >
                          Manual
                        </button>
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Variante para probar</div>
                      <div className="grid grid-cols-3 gap-2">
                        {getActiveTerrainVariantCells().map((cell, index) => {
                          const isSelected = selectedTerrainVariantMode === 'manual' && selectedTerrainVariantIndex === index;
                          return (
                            <button
                              key={`${activeTerrainIndex}-${cell.x}-${cell.y}-${index}`}
                              type="button"
                              onClick={() => {
                                setSelectedTerrainVariantMode('manual');
                                setSelectedTerrainVariantIndex(index);
                              }}
                              className={`aspect-square rounded-lg border overflow-hidden transition-all ${isSelected
                                  ? 'border-game-primary bg-game-primary/10 ring-1 ring-game-primary/40'
                                  : 'border-slate-700 bg-slate-900/60 hover:border-slate-500'
                                }`}
                              title={`Variante ${index + 1}`}
                            >
                              <div
                                className="w-full h-full bg-cover bg-center"
                                style={{
                                  backgroundImage: `url(${resolveTilesetImageSrc(config.image, apiBaseUrl)})`,
                                  backgroundPosition: `-${Number(cell.x || 0) * (config.tileSize || 48)}px -${Number(cell.y || 0) * (config.tileSize || 48)}px`,
                                  backgroundSize: `${(config.tileSize || 48) * (config.columns || 16)}px auto`
                                }}
                              />
                            </button>
                          );
                        })}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        `Auto` usa el comportamiento normal del terreno. `Manual` fuerza una pieza concreta.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-300 font-bold text-sm">Mapa de prueba</div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Pinta para probar</span>
                <div className="flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-900/60 p-1">
                  <button
                    type="button"
                    onClick={() => setTestMapMode('paint')}
                    className={`px-2 py-1 rounded-md text-[11px] font-bold ${testMapMode === 'paint' ? 'bg-cyan-500 text-white' : 'text-slate-300 hover:text-white'}`}
                  >
                    Pintar
                  </button>
                  <button
                    type="button"
                    onClick={() => setTestMapMode('erase')}
                    className={`px-2 py-1 rounded-md text-[11px] font-bold ${testMapMode === 'erase' ? 'bg-rose-500 text-white' : 'text-slate-300 hover:text-white'}`}
                  >
                    Borrar
                  </button>
                </div>
                <button
                  type="button"
                  onClick={fillTestMap}
                  className="px-3 py-1 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-[11px] font-bold"
                >
                  Rellenar
                </button>
                <button
                  type="button"
                  onClick={clearTestMap}
                  className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-[11px] font-bold"
                >
                  Limpiar
                </button>
              </div>
            </div>
            <div className="overflow-auto rounded-2xl border border-slate-800 bg-slate-950/60">
              <canvas
                ref={testMapRef}
                className="w-full h-auto cursor-crosshair"
                onMouseDown={(e) => {
                  const canvas = testMapRef.current;
                  const rect = canvas.getBoundingClientRect();
                  const scaleX = canvas.width / rect.width;
                  const scaleY = canvas.height / rect.height;
                  const x = Math.floor((e.clientX - rect.left) * scaleX / Number(config.tileSize || 48));
                  const y = Math.floor((e.clientY - rect.top) * scaleY / Number(config.tileSize || 48));
                  paintTestMapCell(x, y);
                }}
                onContextMenu={(e) => e.preventDefault()}
                onMouseMove={(e) => {
                  if (e.buttons !== 1) return;
                  const canvas = testMapRef.current;
                  const rect = canvas.getBoundingClientRect();
                  const scaleX = canvas.width / rect.width;
                  const scaleY = canvas.height / rect.height;
                  const x = Math.floor((e.clientX - rect.left) * scaleX / Number(config.tileSize || 48));
                  const y = Math.floor((e.clientY - rect.top) * scaleY / Number(config.tileSize || 48));
                  paintTestMapCell(x, y);
                }}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-slate-300 font-bold text-sm">JSON del tileset</div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Persistible en settings</span>
                <button
                  type="button"
                  onClick={() => setShowJsonEditor((current) => !current)}
                  className="px-3 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-[11px] font-bold"
                >
                  {showJsonEditor ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>
            {showJsonEditor && (
              <>
                <textarea
                  value={jsonPreview}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  className="w-full min-h-[640px] bg-slate-950 border border-slate-800 rounded-2xl p-4 text-xs text-cyan-100 font-mono focus:outline-none focus:border-game-primary"
                  spellCheck="false"
                />
                {error && (
                  <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm p-3">
                    {error}
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TilesetConfigurator;

